import { useTheme } from "styled-components";
import { useLanguageContext } from "../../../../../context/LanguageContext";
import {
  StyledCard,
  StyledCardBody,
  StyledCardHeader,
} from "../../../../../styles/CardStyles";
import {
  StyledFlexColumn,
  StyledFlexRow,
} from "../../../../../styles/LayoutStyles";
import IconSelect from "../../../../../components/select/main/icon/IconSelect";
import {
  faArrowTurnUp,
  faList,
  faMinus,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "../../../../../components/select/main/Select";
import { StyledAltIconButton } from "../../../../../styles/ButtonStyles";
import OverlayLoader from "../../../../../components/loader/OverlayLoader/OverlayLoader";
import { StyledInput } from "../../../../../styles/InputStyles";
import {
  StyledFieldMapping,
  StyledFixedValue,
} from "./AzureFieldMappingsStyles";

function AzureFieldMappingsView({
  availableEntities,
  availableAzureFields,
  availableFields,
  fieldMappings,
  onFieldMappingAdd,
  onAzureFieldChange,
  onAzureValueChange,
  onFieldMappingDelete,
  onFieldValueMappingAdd,
  onFieldValueMappingDelete,
  formId,
}) {
  const theme = useTheme();
  const { translate } = useLanguageContext();

  return (
    <>
      {availableEntities.map((entity) => (
        <OverlayLoader key={entity} groups="azure_configs_page_fetch">
          <StyledCard>
            <StyledCardHeader>
              <StyledFlexRow
                style={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h2 style={{ flex: 1 }}>
                  {`${translate("eva.main.entities.fields")} - ${translate(`eva.main.entities.${entity}`)}`}
                </h2>

                {/* Field mapping add button */}
                <IconSelect
                  options={availableFields[entity].map((field) => ({
                    value: field,
                    label: translate(`eva.main.validation.attributes.${field}`),
                  }))}
                  icon={() => <FontAwesomeIcon icon={faPlus} />}
                  style={{ marginLeft: "auto" }}
                  onChange={(value) => onFieldMappingAdd(entity, value)}
                  disabled={availableFields[entity].length <= 0}
                />
              </StyledFlexRow>
            </StyledCardHeader>

            <StyledCardBody style={{ overflow: "hidden" }}>
              <StyledFlexColumn style={{ gap: theme.spacing.large }}>
                {fieldMappings[entity].map((fieldMapping, index) => (
                  <StyledFieldMapping key={fieldMapping.field}>
                    <StyledFlexRow
                      style={{ flexWrap: "nowrap", overflow: "hidden" }}
                    >
                      {/* Field mapping field */}
                      <StyledFixedValue style={{ flex: 1 }}>
                        {translate(
                          `eva.main.validation.attributes.${fieldMapping.field}`,
                        )}
                      </StyledFixedValue>

                      {/* Field mapping azure field */}
                      <Select
                        value={fieldMapping.azure_field}
                        options={availableAzureFields.map((field) => ({
                          value: field,
                          label: field,
                        }))}
                        style={{ flex: 1, overflow: "hidden" }}
                        onChange={(value) =>
                          onAzureFieldChange(entity, fieldMapping.field, value)
                        }
                      />

                      {/* Field mapping actions */}
                      <StyledFlexRow
                        style={{
                          flexWrap: "nowrap",
                        }}
                      >
                        {/* Field value mapping add button */}
                        <IconSelect
                          options={fieldMapping.availableValues ?? []}
                          icon={() => <FontAwesomeIcon icon={faList} />}
                          onChange={(value) =>
                            onFieldValueMappingAdd(
                              entity,
                              fieldMapping.field,
                              value,
                            )
                          }
                          disabled={
                            (fieldMapping.availableValues?.length ?? 0) <= 0
                          }
                        />

                        {/* Field mapping delete button */}
                        <StyledAltIconButton
                          onClick={() =>
                            onFieldMappingDelete(entity, fieldMapping.field)
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </StyledAltIconButton>
                      </StyledFlexRow>
                    </StyledFlexRow>

                    {/* Field value mappings */}
                    {fieldMapping.value_mappings?.map((valueMapping) => (
                      <StyledFlexRow
                        key={valueMapping[fieldMapping.field]}
                        style={{
                          flexWrap: "nowrap",
                        }}
                      >
                        {/* Field value mapping field */}
                        <StyledFlexRow
                          style={{
                            flex: 1,
                            overflow: "hidden",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            paddingLeft: theme.spacing.normal,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faArrowTurnUp}
                            style={{ transform: "rotate(90deg)" }}
                          />

                          <StyledFixedValue style={{ flex: 1 }}>
                            {valueMapping.label}
                          </StyledFixedValue>
                        </StyledFlexRow>

                        {/* Field value mapping azure value */}
                        <StyledFlexRow
                          style={{
                            flex: 1,
                            flexWrap: "nowrap",
                            alignItems: "center",
                            paddingLeft: theme.spacing.normal,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faArrowTurnUp}
                            style={{ transform: "rotate(90deg)" }}
                          />

                          <StyledInput
                            form={formId}
                            value={valueMapping.azure_value}
                            type="text"
                            placeholder="Azure value"
                            style={{ flex: 1 }}
                            onChange={(e) =>
                              onAzureValueChange(
                                entity,
                                fieldMapping.field,
                                valueMapping[fieldMapping.field],
                                e.target.value,
                              )
                            }
                          />
                        </StyledFlexRow>

                        {/* Field value mapping actions */}
                        <StyledFlexRow style={{ flexWrap: "nowrap" }}>
                          <div style={{ width: theme.container.size.normal }} />

                          {/* Field value mapping delete button */}
                          <StyledAltIconButton
                            onClick={() =>
                              onFieldValueMappingDelete(
                                entity,
                                fieldMapping.field,
                                valueMapping[fieldMapping.field],
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </StyledAltIconButton>
                        </StyledFlexRow>
                      </StyledFlexRow>
                    ))}
                  </StyledFieldMapping>
                ))}
              </StyledFlexColumn>
            </StyledCardBody>
          </StyledCard>
        </OverlayLoader>
      ))}
    </>
  );
}

export default AzureFieldMappingsView;
